import {Button, colors, Typography} from 'nf-ui'
import SvgCaret from 'nf-ui/Icons/Caret'
import {motion} from 'framer-motion'
import React, {useMemo} from 'react'
import styled from 'styled-components'
import {useQueryParam} from 'use-query-params'
import {Profile} from '../useFullOrgChartData'
import {ProfileRowAction} from '../FullOrgChart/ProfileRowAction'
import {ProfileThumbnail} from '../FullOrgChart/ProfileBrick/ProfileThumbnail'
import {PROFILE_ROW_PADDING} from '../FullOrgChart/ProfileBrick/constants'
import {useProfileDetails} from '../FullOrgChart/useProfileDetails'

const ItemContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
`

const ProfileWrapper = styled.div`
    position: relative;
    z-index: 1;
    width: 120px;
`

const ProfileRowContainer = styled.div`
    position: relative;
    border: 1px solid ${colors.darkGray};
    padding: ${PROFILE_ROW_PADDING}px 6px;
    box-sizing: border-box;
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    background-color: ${colors.lightGray};
    width: 100%;

    :hover {
        background-color: #edeff0;
    }
`

const PhotoContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 8px;
`

const ProfileContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 8px;
    width: 100%;
    min-height: 35px;
`

const ProfileName = styled(Typography.Label)`
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    white-space: nowrap;
    max-width: 100%;
    line-height: 19px;
    margin-bottom: 4px;
`

const SecondaryLine = styled(Typography.Tiny)`
    line-height: 15px;
    min-height: 15px;
    margin-top: 3px;
    overflow-x: hidden;
    overflow-y: visible;
    text-overflow: ellipsis;
    text-align: center;
    white-space: nowrap;
    max-width: 100%;
    display: flex;
`

const BadgeContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0;
    width: 100%;
    justify-content: center;
`

const CountText = styled(Typography.Tiny)`
    margin: 0 4px;
    padding: 0;
    color: ${colors.primary[100]};
    font-weight: bold;
`

const CaretBorder = styled.div`
    height: 24px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${colors.transparent};
    border-radius: 3px;
    transition: border-color 0.3s ease-in-out;

    :hover {
        border-color: ${colors.primary[35]};
    }
`

const CaretButton = styled(Button)`
    height: auto;
    flex-shrink: 0;
    padding: 0;
    margin-right: 3px;
`

type HorizontalProfileItemProps = {
    profile: Profile
    getProfileFromIdStr: (idStr: string) => Profile | null
    isExpanded: boolean
    toggleExpand: () => void
    setRef: (el: HTMLDivElement | null) => void
}

export const HorizontalProfileItem: React.FC<HorizontalProfileItemProps> = ({
    profile,
    getProfileFromIdStr,
    isExpanded,
    toggleExpand,
    setRef,
}) => {
    const [, setProfileId] = useQueryParam<string>('profileId')
    const {heading, secondaryLine: profileSecondaryLine} = useProfileDetails(profile)

    const directReports = useMemo(() => {
        return [
            ...profile.directReports.map((idStr) => getProfileFromIdStr(idStr)),
            ...profile.additionalReports.map((idStr) => getProfileFromIdStr(idStr)),
        ].filter((p): p is Profile => p !== null)
    }, [profile, getProfileFromIdStr])

    const reportsCount = directReports.length
    const hasReports = reportsCount > 0
    const canShowBranch = hasReports && !profile.additionalReports.length

    const handleProfileClick = () => {
        setProfileId(profile.idStr)
    }

    return (
        <ItemContainer id={`horizontal-profile-${profile.idStr}`} ref={setRef}>
            <ProfileWrapper>
                <ProfileRowContainer onClick={handleProfileClick}>
                    <PhotoContainer>
                        <ProfileThumbnail profile={profile} />
                    </PhotoContainer>
                    <ProfileContent>
                        <ProfileName>{heading}</ProfileName>
                        <SecondaryLine>{profileSecondaryLine}</SecondaryLine>
                    </ProfileContent>
                    <BadgeContainer>
                        <div
                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                // Ellipsis interactions must not open profile side panel.
                                event.preventDefault()
                                event.stopPropagation()
                            }}
                        >
                            <ProfileRowAction
                                profile={profile}
                                canShowBranch={canShowBranch}
                                compact={false}
                                noLeadingSpacer={true}
                                noBorder={true}
                            />
                        </div>
                        {hasReports && <CountText>{reportsCount}</CountText>}
                        {hasReports && (
                            <CaretButton
                                variant="link"
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                    // Toggle should not open the profile details panel.
                                    event.preventDefault()
                                    event.stopPropagation()
                                    toggleExpand()
                                }}
                            >
                                <CaretBorder>
                                    <motion.div
                                        initial={isExpanded ? 'expanded' : 'collapsed'}
                                        animate={isExpanded ? 'expanded' : 'collapsed'}
                                        variants={{
                                            collapsed: {rotate: 0},
                                            expanded: {rotate: 180},
                                        }}
                                    >
                                        <Button.Icon icon={SvgCaret} />
                                    </motion.div>
                                </CaretBorder>
                            </CaretButton>
                        )}
                    </BadgeContainer>
                </ProfileRowContainer>
            </ProfileWrapper>
        </ItemContainer>
    )
}
